// ...existing imports...
import { HttpService } from './../services/http.service';
import { Router } from '@angular/router';
import { GasProjectDialogComponent } from './../gas-project-dialog/gas-project-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { DataService } from './../services/data.service';
import { ActivatedRoute } from '@angular/router';
import { RoleTestingService } from '../services/role-testing.service';
import { environment } from '../../environments/environment';

const APPROVER_STATUS_TTL_MS = 365 * 24 * 60 * 60 * 1000;
const IT_APPROVER_TTL_KEY = 'mmaps_it_approver_status_expires_at';
const RE_APPROVER_TTL_KEY = 'mmaps_re_approver_status_expires_at';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: false,
})
export class DashboardComponent implements OnInit {
  firstName: string;
  userRoles: string[] = [];
  currentUserEmail: string;
  formId: string | null = null;
  checkingApproverStatus = false;
  private mmapsITApproverStatus: string = '';
  private mmapsREApproverStatus: string = '';
  environment = environment;

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private http: HttpService,
    private dataService: DataService,
    private route: ActivatedRoute,
    private roleTestingService: RoleTestingService,
    private cdr: ChangeDetectorRef // <-- inject ChangeDetectorRef
  ) {}

  private setApproverStatusWithTTL(type: 'it' | 're') {
    const expiresAt = (Date.now() + APPROVER_STATUS_TTL_MS).toString();
    if (type === 'it') {
      sessionStorage.setItem('mmaps_it_approver_status', 'true');
      localStorage.setItem(IT_APPROVER_TTL_KEY, expiresAt);
      return;
    }
    sessionStorage.setItem('mmaps_re_approver_status', 'true');
    localStorage.setItem(RE_APPROVER_TTL_KEY, expiresAt);
  }

  // Returns true if user is approver only (no roles, but has approver status)
  get isApproverOnly(): boolean {
    const roles = this.userRoles || [];
    const itApproverStatus = sessionStorage.getItem('mmaps_it_approver_status');
    const reApproverStatus = sessionStorage.getItem('mmaps_re_approver_status');
    return roles.length === 0 && (itApproverStatus === 'true' || reApproverStatus === 'true');
  }

  get isITApproverOnly(): boolean {
    // Approver-only, no roles, and not a Real Estate approver
    const roles = this.userRoles || [];
    const approverStatus = sessionStorage.getItem('mmaps_it_approver_status');
    // If user has no roles and approver status, but not a Real Estate user
    return roles.length === 0 && approverStatus === 'true' && !this.isRealEstateApproverOnly;
  }

  get isRealEstateApproverOnly(): boolean {
    // Approver-only, no roles, and not an IT approver
    const roles = this.userRoles || [];
    const approverStatus = sessionStorage.getItem('mmaps_re_approver_status');
    // If user has no roles and approver status, but not an IT user
    // For now, treat as Real Estate if not IT (since we can't distinguish without more info)
    return roles.length === 0 && approverStatus === 'true';
  }

  async ngOnInit() {
    this.getEcosysData();
    if (!environment.production) {
      (window as any).dashboardComponent = this;
    }
    let userData = JSON.parse(sessionStorage.getItem('userInfo'));
    this.currentUserEmail = userData.account.username || userData.preferred_username;

    // Apply role testing override if enabled
    this.userRoles = this.roleTestingService.applyRoleTestingOverride(
      (userData.idTokenClaims && userData.idTokenClaims.roles) || []
    );

    // Check for form ID in URL parameters (for approver links)
    this.route.queryParams.subscribe((params) => {
      this.formId = params['pId'] || null;
      if (this.formId) {
        // Approver status should be set elsewhere (e.g., after API call)
      }
    });

    if (userData) {
      let stringArray = userData.account.name.split(',');
      this.firstName = stringArray[1].substring(1);
    } else {
      this.signOut();
    }
    // Refresh dynamic approver flags whenever dashboard loads
    this.refreshDynamicApproverStatus(userData && userData.account ? userData.account.name : '');

    // Watch for changes to dedicated approver flags
    this.mmapsITApproverStatus = sessionStorage.getItem('mmaps_it_approver_status');
    this.mmapsREApproverStatus = sessionStorage.getItem('mmaps_re_approver_status');
    window.addEventListener('storage', this.handleStorageChange.bind(this));
  }

  private refreshDynamicApproverStatus(accountName: string) {
    const userEmail = (this.currentUserEmail || '').toLowerCase();
    if (!userEmail) {
      return;
    }

    const emailAlias = userEmail.includes('@') ? userEmail.split('@')[0] : userEmail;
    const displayName = (accountName || '').trim().toLowerCase();
    const matchEmail = (raw: any) => {
      if (!raw) return false;
      const norm = String(raw).toLowerCase();
      return norm === userEmail || norm === emailAlias;
    };

    this.dataService.getItSparData({}, 'reviews').subscribe(
      (itRes: any) => {
        const itForms = itRes && itRes.body ? itRes.body : [];
        const isITApprover =
          Array.isArray(itForms) &&
          itForms.some(
            (form: any) =>
              (form.itSponsor && matchEmail(form.itSponsor.mail || form.itSponsor.mailNickname)) ||
              (form.approvers &&
                form.approvers.some((a: any) => matchEmail(a.email || a.mailNickname || a.mail))) ||
              (form.jointOwnerCoordinator && matchEmail(form.jointOwnerCoordinator.mail)) ||
              (form.currentAssignment &&
                matchEmail(form.currentAssignment.mail || form.currentAssignment.mailNickname)) ||
              (form.currentApprover &&
                matchEmail(form.currentApprover.mail || form.currentApprover.mailNickname))
          );

        if (isITApprover) {
          this.setApproverStatusWithTTL('it');
        } else {
          if (sessionStorage.getItem('mmaps_it_approver_status') !== 'true') {
            sessionStorage.removeItem('mmaps_it_approver_status');
          }
        }

        this.dataService.getRREData().subscribe(
          (reRes: any) => {
            const projects = reRes && reRes.projects ? reRes.projects : reRes || [];
            const isREApprover =
              Array.isArray(projects) &&
              projects.some((project: any) => {
                const assignedTo = project.assigned_to;
                const assignedValues = Array.isArray(assignedTo)
                  ? assignedTo.map((a: any) =>
                      typeof a === 'string' ? a.trim().toLowerCase() : ''
                    )
                  : typeof assignedTo === 'string'
                    ? [assignedTo.trim().toLowerCase()]
                    : [];
                return (
                  assignedValues.includes(userEmail) ||
                  (displayName && assignedValues.includes(displayName))
                );
              });

            if (isREApprover) {
              this.setApproverStatusWithTTL('re');
            } else {
              if (sessionStorage.getItem('mmaps_re_approver_status') !== 'true') {
                sessionStorage.removeItem('mmaps_re_approver_status');
              }
            }

            this.mmapsITApproverStatus = sessionStorage.getItem('mmaps_it_approver_status');
            this.mmapsREApproverStatus = sessionStorage.getItem('mmaps_re_approver_status');
            this.cdr.detectChanges();
          },
          () => {
            this.mmapsREApproverStatus = '';
            this.cdr.detectChanges();
          }
        );
      },
      () => {
        this.mmapsITApproverStatus = '';
        this.cdr.detectChanges();
      }
    );
  }

  ngOnDestroy() {
    window.removeEventListener('storage', this.handleStorageChange.bind(this));
  }

  private handleStorageChange(event: StorageEvent) {
    if (event.key === 'mmaps_it_approver_status' || event.key === 'mmaps_re_approver_status') {
      this.mmapsITApproverStatus = sessionStorage.getItem('mmaps_it_approver_status');
      this.mmapsREApproverStatus = sessionStorage.getItem('mmaps_re_approver_status');
      this.cdr.detectChanges();
    }
  }

  getEcosysData() {
    this.dataService.getEcosysData();
  }

  openGasProjectDialog() {
    this.dialog.open(GasProjectDialogComponent, {
      disableClose: true,
      width: '21.875rem',
      height: '23.063rem',
    });
  }

  signOut() {
    this.router.navigate(['/login']);
  }

  submitTEST() {
    let callBody;
    this.http.get('assets/sample-submit.json').subscribe((result) => {
      callBody = result;
      this.submitTEST2(callBody);
    });
  }

  submitTEST2(callBody: any) {
    this.dataService.submitProjectTEST(callBody).subscribe((result) => {
      console.log('test done', result);
    });
  }

  openITProjectSetup() {
    const userData = sessionStorage.getItem('userInfo');
    const currentUser = sessionStorage.getItem('currentUser');
    const fullTokenPayload = sessionStorage.getItem('fullTokenPayload');
    const itApproverStatus = sessionStorage.getItem('mmaps_it_approver_status');
    const reApproverStatus = sessionStorage.getItem('mmaps_re_approver_status');
    sessionStorage.clear();
    if (userData) {
      sessionStorage.setItem('userInfo', userData);
    }
    if (currentUser) {
      sessionStorage.setItem('currentUser', currentUser);
    }
    if (fullTokenPayload) {
      sessionStorage.setItem('fullTokenPayload', fullTokenPayload);
    }
    if (itApproverStatus) {
      sessionStorage.setItem('mmaps_it_approver_status', itApproverStatus);
    }
    if (reApproverStatus) {
      sessionStorage.setItem('mmaps_re_approver_status', reApproverStatus);
    }

    this.router.navigate(['/it-project-setup']);
  }

  openRREProjectSetup() {
    this.router.navigate(['/rre-projects']);
  }

  openRealEstateProjectSetup() {
    // If user is accessing via approver link, navigate with the project ID
    if (this.formId) {
      this.router.navigate(['/real-estate-project'], {
        queryParams: { pId: this.formId },
      });
    } else {
      this.router.navigate(['/real-estate-project']);
    }
  }

  hasGasAccess() {
    return this.userRoles.includes('MMAPS_Gas_Users');
  }

  hasOnlyGasAccess() {
    return (
      this.hasGasAccess() &&
      !this.hasETSAccess() &&
      !this.hasGenerationAccess() &&
      !this.hasRealEstateAccess()
    );
  }

  hasETSAccess() {
    return (
      this.userRoles.includes('MMAPS_ETS_Users') ||
      this.userRoles.includes('MMAPS_ETS_Asset_Users') ||
      this.userRoles.includes('MMAPS_ETS_PAC_Users') ||
      this.userRoles.includes('MMAPS_ETS_RO_Users') ||
      sessionStorage.getItem('mmaps_it_approver_status') === 'true'
    );
  }

  hasGenerationAccess() {
    return this.userRoles.includes('MMAPS_Generation_Users');
  }

  hasRealEstateAccess() {
    // Real estate access: role or approver status
    return (
      this.userRoles.includes('MMAPS_Real_Estate_Users') ||
      sessionStorage.getItem('mmaps_re_approver_status') === 'true'
    );
  }

  hasApproverAccess() {
    // Approver status for current form
    return (
      sessionStorage.getItem('mmaps_it_approver_status') === 'true' ||
      sessionStorage.getItem('mmaps_re_approver_status') === 'true'
    );
  }

  hasAnyAccess() {
    return (
      this.hasGasAccess() ||
      this.hasETSAccess() ||
      this.hasGenerationAccess() ||
      this.hasRealEstateAccess()
    );
  }

  // Approver logic handled via sessionStorage and userRoles
}
